import Backbone from 'backbone';
import MembershipNav from './membership-nav';
import Overlay from './overlay';
import SearchSubmenu from './search-submenu';
import { TourStopSearchForm, tourHeaderSelectorDesktop } from './tour-stop-search-form';
import { reflow } from './util';
import { getFocusables, onTabKeyDownCallback } from '../utils/header-focusable';

const Header = Backbone.View.extend({
  initialize() {
    this.primaryNav    = new PrimaryNav({ el: '[data-primary-nav]' });
    this.membershipNav = new MembershipNav({ el: '[data-membership-nav]'});
    this.overlay       = new Overlay({ el: '[data-header-overlay]' });
    this.searchSubmenu = new SearchSubmenu({ el: '[data-header-desktop] [data-search-submenu-form]' });

    if (document.querySelector(tourHeaderSelectorDesktop)) {
      this.tourStopSearchForm = new TourStopSearchForm({ el: tourHeaderSelectorDesktop });
    }

    this.initListeners();
  },
  initListeners() {
    this.listenTo(Backbone, {
      'primary-nav:submenu:open':  this.collapse,
      'primary-nav:submenu:close': this.expand
    });
  },
  resetToInitialState() {
    document.body.classList.remove('layout/overflow:hidden');
    this.el.classList.remove('header--opened', 'header--partial');
  },
  collapse() {
    this.el.classList.add('header--opened', 'header--partial');
  },
  expand() {
    this.el.classList.remove('header--opened', 'header--partial');
  },
  dispose() {
    this.primaryNav.dispose();
    this.membershipNav.dispose();
    this.overlay.dispose();
    this.searchSubmenu.dispose();
    if (this.tourStopSearchForm) this.tourStopSearchForm.dispose();

    this.primaryNav = null;
    this.membershipNav = null;
    this.overlay = null;
    this.searchSubmenu = null;
    if (this.tourStopSearchForm) this.tourStopSearchForm = null;

    this.undelegateEvents();
    this.stopListening();
  },
});

const PrimaryNav = Backbone.View.extend({
  events: {
    'click [data-submenu-trigger]': 'onClickSubmenuTrigger',
  },
  initialize() {
    this.zIndex         = getComputedStyle(this.el).zIndex;
    this.selectedPrimaryNav = this.getSelectedPrimaryNavItem();
    this.initListeners();

    // If page meta has active context in the primary nav, highlight that item.
    this.selectedPrimaryNav?.classList.add('typography/underline');
  },
  initListeners() {
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnFadeInTransitionEnd = this.onFadeInTransitionEnd.bind(this);
    this.boundOnFadeOutTransitionEnd = this.onFadeOutTransitionEnd.bind(this);
    this.listenTo(Backbone, {
      'overlay:click':  this.closeSubmenu,
      'overlay:cancel': this.closeSubmenu
    });
  },
  getSelectedPrimaryNavItem() {
    const pageContext = document.querySelector("[data-current-primary-nav-item]")?.getAttribute("data-current-primary-nav-item");
    if (!pageContext) return null;

    const navItemSelector = '[data-header-primary-nav-item="' + pageContext + '"] button span.typography';
    const navItem = this.$(navItemSelector)[0];
    return navItem;
  },
  onClickSubmenuTrigger(e) {
    this.submenuTrigger     = e.currentTarget;
    this.submenuTriggerSpan = this.submenuTrigger.querySelector('span.typography');
    this.submenu            = this.submenuTrigger.nextElementSibling;

    if (this.submenuTrigger.getAttribute('aria-expanded') === 'true') {
      this.closeSubmenu();
    } else {
      this.openSubmenu();
    }
  },
  openSubmenu() {
    if (this.submenu && this.submenuTrigger) {
      this.clearTransitionEndListeners();
      this.closeAllSubmenus();

      // Set submenu trigger active by rendering an underline.
      this.submenuTriggerSpan.classList.add("typography/underline");

      // Trigger submenu open transition.
      this.submenu.style.display = 'block';
      reflow(this.submenu);
      this.submenu.classList.add('visible');
      this.submenuTrigger.setAttribute('aria-expanded', true);

      this.submenu.addEventListener('transitionend', this.boundOnFadeInTransitionEnd);
      document.addEventListener('keydown', this.boundOnKeyDown);

      Backbone.trigger('primary-nav:submenu:open', { zIndex: this.zIndex });
      Backbone.trigger('primary-nav:submenu:opened', { activeSubmenuId: this.submenu.id });
    }
  },
  closeSubmenu() {
    if (this.submenu && this.submenuTrigger) {
      this.clearTransitionEndListeners();

      // Set submenu trigger inactive by remove underline.
      this.submenuTriggerSpan.classList.remove("typography/underline");

      // Return active underline to page's active context.
      this.selectedPrimaryNav?.classList.add('typography/underline');
      this.submenuTrigger.removeAttribute('aria-expanded');
      this.submenu.addEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
      this.submenu.classList.remove('visible');
      document.removeEventListener('keydown', this.boundOnKeyDown);

      Backbone.trigger('primary-nav:submenu:close');
    }
  },
  closeAllSubmenus() {
    document.querySelectorAll('[data-submenu-trigger]').forEach((el) => {
      if (el === this.submenuTrigger) return;
      el.removeAttribute('aria-expanded');
    });
    document.querySelectorAll('[data-submenu]').forEach((el) => {
      if (el === this.submenu) return;
      el.style.display = 'none';
    });
    document.querySelectorAll('[data-submenu-trigger] span').forEach((el) => {
      if (el === this.submenuTriggerSpan) return;
      el.classList.remove('typography/underline');
    });
  },
  resetAllSubmenus() {
    document.querySelectorAll('[data-submenu]').forEach((el) => {
      el.style.display = '';
    });
  },
  clearTransitionEndListeners() {
    if (this.submenu) {
      this.submenu.removeEventListener('transitionend', this.boundOnFadeInTransitionEnd);
      this.submenu.removeEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
    }
  },
  onFadeInTransitionEnd(e) {
    if (e.propertyName === 'opacity') {
      this.focusables = getFocusables(this.submenu);
      if (this.focusables.length > 0) this.focusables[0].focus();
    }
    if (this.submenu) this.submenu.removeEventListener('transitionend', this.boundOnFadeInTransitionEnd);
  },
  onFadeOutTransitionEnd() {
    if (this.submenu) {
      this.submenu.removeEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
      this.submenu.style.display = '';
    }
    this.resetAllSubmenus();
    this.focusables = [];
    this.submenuTrigger = null;
    this.submenuTriggerSpan = null;
    this.submenu = null;
  },
  onKeyDown(e) {
    switch (e.key) {
      case 'Tab':
        if (this.focusables) onTabKeyDownCallback(e, this.focusables, this.closeSubmenu.bind(this));
        break;
      case 'Escape':
        this.closeSubmenu();
        break;
    }
  },
  dispose() {
    this.closeSubmenu();
    this.resetAllSubmenus();
    this.clearTransitionEndListeners();
    this.undelegateEvents();
    this.stopListening();
  },
});

export default Header;
